import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Card,
  CardMedia,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment
} from '@mui/material';
import {
  Check as CheckIcon,
  Info as InfoIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { api } from '../../utils/api';
import { getProductImage } from '../../utils/productUtils';
import QuantityReceivedDialog from './QuantityReceivedDialog';

const PendingDeliveryTab = () => {
  const [pendingItems, setPendingItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [newProductDialog, setNewProductDialog] = useState(false);
  const [quantityReceivedDialog, setQuantityReceivedDialog] = useState(false);
  const [priceUpdateDialog, setPriceUpdateDialog] = useState(false);
  const [priceData, setPriceData] = useState({
    oldPurchasePrice: 0,
    newPurchasePrice: 0,
    updatedPurchasePrice: 0,
    updatedSellingPrice: 0,
    assemblyTime: 0,
    laborHourlyRate: 30,
    sellingPriceWithLabor: 0,
    quantityLoaded: 0,
    action: ''
  });
  const [newProductData, setNewProductData] = useState({
    assemblyTime: '',
    sellingPrice: '',
    suggestedPrice: 0,
    totalPriceWithLabor: 0,
    category: 'Generale',
    minStock: 1,
    b2bProduct: null,
    brand: '',
    name: '',
    description: '',
    quantityReceived: '',
    purchasePrice: ''
  });
  const [categories, setCategories] = useState([]);
  const [laborCost, setLaborCost] = useState(30); // Costo orario di default (€/ora)
  const [loadingItemCode, setLoadingItemCode] = useState(null);

  useEffect(() => {
    loadPendingItems();
    loadCategories();
    loadLaborCost();
  }, []);

  const loadLaborCost = async () => {
    try {
      const companyId = localStorage.getItem('companyId');
      if (!companyId) {
        throw new Error('CompanyId not found in localStorage');
      }

      const response = await api.get('/company-settings', {
        headers: {
          'x-company-id': companyId
        }
      });

      if (response.data && response.data.laborHourlyRate) {
        const cost = parseFloat(response.data.laborHourlyRate);
        console.log(`Costo manodopera caricato: €${cost}/ora`);
        setLaborCost(cost);
      } else {
        console.log('Nessun costo manodopera trovato, uso il valore predefinito di €30/ora');
      }
    } catch (err) {
      console.error('Error fetching labor cost:', err);
      // Provo a caricare direttamente l'impostazione come alternativa
      try {
        const fallbackResponse = await api.get('/settings/laborHourlyRate');
        if (fallbackResponse.data && fallbackResponse.data.value) {
          const cost = parseFloat(fallbackResponse.data.value);
          console.log(`Costo manodopera caricato (metodo alternativo): €${cost}/ora`);
          setLaborCost(cost);
        } else {
          console.log('Nessun costo manodopera trovato nemmeno con metodo alternativo');
        }
      } catch (fallbackErr) {
        console.log('Mantengo il valore predefinito di €30/ora');
      }
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/products/categories/all');
      if (response.data && Array.isArray(response.data)) {
        // Ordina le categorie alfabeticamente, mantenendo 'Generale' in cima
        const sortedCategories = response.data
          .filter(cat => cat !== 'Generale')
          .sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
        setCategories(['Generale', ...sortedCategories]);
      }
    } catch (err) {
      console.error('Errore nel caricamento delle categorie:', err);
      setCategories(['Generale']);
    }
  };

  const loadPendingItems = async () => {
    setLoading(true);
    try {
      const response = await api.get('/orders/pending-items');
      setPendingItems(response.data);
      console.log('🧪 Dati aggiornati dopo loadPendingItems:', response.data);
    } catch (err) {
      console.error('Errore nel caricamento degli articoli:', err);
      setSnackbar({
        open: true,
        message: 'Errore nel caricamento degli articoli da ricevere',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReceiveItem = async (item) => {
    try {
      setLoadingItemCode(item.code);
      
      // Usa il codice completo (incluso eventuale prefisso del fornitore)
      const productResponse = await api.get(`/products/check/${item.code}`);
      
      // ✅ Caso 1: Prodotto già esistente → apri dialog per quantità
      if (productResponse.data.exists) {
        console.log("✅ Prodotto già esistente:", productResponse.data.product);
        
        const existingProduct = productResponse.data.product;
        
        // Confronta i prezzi di acquisto
        const existingPrice = parseFloat(existingProduct.purchasePrice) || 0;
        const newPrice = parseFloat(item.price) || 0;
        
        // Salva il prodotto selezionato
        setSelectedItem({
          ...item,
          existingProduct
        });
        
        // Se il prezzo è cambiato, mostra il dialog per la gestione dei prezzi
        if (Math.abs(existingPrice - newPrice) > 0.01) {
          // Se il prezzo è cambiato, apri il dialog per il prezzo
          setPriceData({
            oldPurchasePrice: existingPrice,
            newPurchasePrice: newPrice,
            updatedPurchasePrice: existingPrice,
            updatedSellingPrice: parseFloat(existingProduct.sellingPrice) || 0,
            assemblyTime: parseFloat(existingProduct.assemblyTime) || 0,
            laborHourlyRate: laborCost,
            sellingPriceWithLabor: 0,
            quantityLoaded: item.quantity - (item.receivedQuantity || 0),
            action: ''
          });
          setPriceUpdateDialog(true);
            } else {
          // Altrimenti apri direttamente il dialog per la quantità
          setQuantityReceivedDialog(true);
        }
        return;
      }

      // ❌ Caso 2: Prodotto non esistente → apri dialog creazione
      const b2bRes = await api.get(`/b2b-products/code/${encodeURIComponent(item.code)}`);
      const b2bProduct = b2bRes.data;

      if (!b2bProduct) {
        setSnackbar({
          open: true,
          message: 'Prodotto non trovato nel listino B2B',
          severity: 'error'
        });
        return;
      }

      // Imposta il prodotto da creare e apri dialog
      setSelectedItem({
        ...item,
        companyId: 1
      });
      
      // Prepara i dati per il nuovo prodotto
        const defaultAssemblyTime = 15;
        const laborPricePerMinute = laborCost / 60;
        const laborPrice = laborPricePerMinute * defaultAssemblyTime;
        
      let suggestedPrice = parseFloat(b2bProduct.sellingPrice || 0);
      let basePrice = suggestedPrice || parseFloat(item.price || 0);
      let purchasePrice = parseFloat(b2bProduct.purchasePrice || item.price || 0);
      
      // Assicurati che la categoria sia sempre presente
      const productCategory = b2bProduct.category || 'GENERALE';
          
          setNewProductData({
            assemblyTime: defaultAssemblyTime.toString(),
            sellingPrice: basePrice.toFixed(2),
            suggestedPrice: suggestedPrice,
            totalPriceWithLabor: (basePrice + laborPrice).toFixed(2),
        category: productCategory.toUpperCase(),
            minStock: b2bProduct.minQty || '1',
            b2bProduct: b2bProduct,
            brand: b2bProduct.brand || '',
            name: b2bProduct.name || item.description,
            description: b2bProduct.description || item.description,
            quantityReceived: item.quantity || '1',
            purchasePrice: purchasePrice.toFixed(2)
          });
          
      // Apri il dialog per creare il nuovo prodotto
        setNewProductDialog(true);

    } catch (err) {
      console.error("Errore nella ricezione articolo:", err);
      setSnackbar({
        open: true,
        message: 'Errore durante la ricezione: ' + (err.response?.data?.message || err.message),
        severity: 'error'
      });
    } finally {
      setLoadingItemCode(null);
    }
  };

  // Funzione per calcolare il prezzo della manodopera
  const calculateLaborPrice = (minutes) => {
    const laborPricePerMinute = laborCost / 60; // Costo al minuto
    return laborPricePerMinute * minutes;
  };

  // Gestisce il cambio del tempo di montaggio e ricalcola il prezzo totale
  const handleAssemblyTimeChange = (e) => {
    const assemblyTime = parseFloat(e.target.value || 0);
    const sellingPrice = parseFloat(newProductData.sellingPrice || 0);
    const laborPrice = calculateLaborPrice(assemblyTime);
    
    setNewProductData({
      ...newProductData,
      assemblyTime: e.target.value,
      totalPriceWithLabor: (sellingPrice + laborPrice).toFixed(2)
    });
  };

  // Gestisce il cambio del prezzo di vendita e ricalcola il prezzo totale
  const handleSellingPriceChange = (e) => {
    const sellingPrice = parseFloat(e.target.value || 0);
    const assemblyTime = parseFloat(newProductData.assemblyTime || 0);
    const laborPrice = calculateLaborPrice(assemblyTime);
    
    setNewProductData({
      ...newProductData,
      sellingPrice: e.target.value,
      totalPriceWithLabor: (sellingPrice + laborPrice).toFixed(2)
    });
  };

  // Gestisce il cambio del prezzo totale e ricalcola il tempo di montaggio
  const handleTotalPriceChange = (e) => {
    const totalPrice = parseFloat(e.target.value || 0);
    const sellingPrice = parseFloat(newProductData.sellingPrice || 0);
    
    // Se il prezzo totale è inferiore al prezzo di vendita, non possiamo calcolare un tempo di montaggio valido
    if (totalPrice <= sellingPrice) {
      setNewProductData({
        ...newProductData,
        totalPriceWithLabor: e.target.value,
        assemblyTime: '0' // Nessun tempo di montaggio
      });
      return;
    }
    
    // Calcola il tempo di montaggio basato sul prezzo totale e sul prezzo di vendita
    const laborPrice = totalPrice - sellingPrice;
    const laborPricePerMinute = laborCost / 60;
    const calculatedAssemblyTime = laborPrice / laborPricePerMinute; // Supporta decimali
    
    setNewProductData({
      ...newProductData,
      totalPriceWithLabor: e.target.value,
      assemblyTime: calculatedAssemblyTime.toFixed(2) // Mantiene 2 decimali
    });
  };

  const handleQuantityReceivedChange = (e) => {
    const value = e.target.value;
    const maxQuantity = selectedItem.quantity;
    
    if (value === '' || /^[1-9]\d*$/.test(value)) {
      if (parseInt(value) > maxQuantity) {
        setNewProductData(prev => ({
          ...prev,
          quantityReceived: value,
          quantityError: `La quantità non può superare ${maxQuantity}`
        }));
      } else {
        setNewProductData(prev => ({
          ...prev,
          quantityReceived: value,
          quantityError: ''
        }));
      }
    }
  };

  // Aggiungo funzione mancante per gestire la conferma della variazione di prezzo
  const handleConfirmVariation = () => {
    // Per la conferma con il nuovo prezzo, usiamo il prezzo non modificato
    setPriceData(prev => ({
      ...prev,
      updatedPurchasePrice: priceData.newPurchasePrice,
      action: 'new'
    }));
    
    // Chiudi questo dialog e apri quello per la quantità
    setPriceUpdateDialog(false);
    setQuantityReceivedDialog(true);
  };

  // Aggiungo funzione mancante per gestire la conferma del prezzo ponderato
  const handleConfirmPonderatedPrice = () => {
    confirmPrices();
  };

  const handleQuantityReceived = async (quantity) => {
    try {
      console.log('Ricezione articolo:', selectedItem);
      console.log('Quantità ricevuta:', quantity);
      console.log('OrderId:', selectedItem.orderId);
      console.log('Code:', selectedItem.code);
      
      // Determina il productId corretto
      let productId;
      if (selectedItem.existingProduct) {
        productId = selectedItem.existingProduct.id;
        console.log('Usando ID da existingProduct:', productId);
      } else if (selectedItem.productId) {
        productId = selectedItem.productId;
        console.log('Usando ID da productId:', productId);
      } else {
        throw new Error('ID prodotto non trovato');
      }
      
      // Prende il prezzo di acquisto dal carico attuale
      const purchasePrice = parseFloat(selectedItem.price) || 0;
      console.log('Prezzo di acquisto per il carico:', purchasePrice);
      
      // Segna l'articolo come ricevuto usando il codice completo
      const response = await api.put(`/orders/items/${selectedItem.orderId}/${selectedItem.code}/received`, {
        quantityReceived: quantity,
        productId,
        purchasePrice // Invia il prezzo di acquisto dal carico attuale
      });
      
      console.log('Risposta ricezione:', response.data);
      
      // IMPORTANTE: Non chiamare manualmente warehouse-movements/load qui!
      // Il backend (routes/orders.js) già aggiorna l'inventario e crea il movimento di magazzino 
      // quando un articolo viene segnato come ricevuto. Una chiamata aggiuntiva causerebbe un doppio caricamento.
      
      // Aggiorna la lista degli articoli da ricevere
      await loadPendingItems();
      
      setSnackbar({
        open: true,
        message: 'Articolo ricevuto con successo',
        severity: 'success'
      });
    } catch (err) {
      console.error('Errore nella ricezione dell\'articolo:', err);
      console.error('Dettagli errore:', err.response?.data);
      console.error('Status code:', err.response?.status);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Errore nella ricezione dell\'articolo',
        severity: 'error'
      });
    } finally {
      setQuantityReceivedDialog(false);
      setSelectedItem(null);
    }
  };

  const handleAddNewProduct = async () => {
    try {
      // Manteniamo il codice completo con il prefisso del fornitore
      let productCode = selectedItem.code;

      // Se abbiamo un prodotto B2B, usiamo il suo code completo
      if (newProductData.b2bProduct) {
        productCode = newProductData.b2bProduct.code;
      }

      const productData = {
        code: productCode, // Preserviamo il codice completo con il prefisso
        name: newProductData.name.trim().toUpperCase(),
        description: (newProductData.description || '').trim().toUpperCase(),
        sellingPrice: parseFloat(newProductData.sellingPrice) || 0,
        price: parseFloat(newProductData.sellingPrice) || 0,
        purchasePrice: parseFloat(newProductData.purchasePrice) || 0,
        category: newProductData.category.trim().toUpperCase(),
        minStock: parseInt(newProductData.minStock) || 1,
        brand: (newProductData.brand || '').trim().toUpperCase(),
        assemblyTime: parseFloat(newProductData.assemblyTime) || 0, // Supporta decimali
        stock: 0, // Inizializziamo lo stock a 0, verrà aggiornato dalla ricezione ordine
        isActive: true,
        image: selectedItem.image || null
      };

      console.log('CREAZIONE PRODOTTO - Endpoint: /products');
      console.log('CREAZIONE PRODOTTO - Payload:', JSON.stringify(productData, null, 2));

      const response = await api.post('/products', productData);

      if (response.data) {
        // Segna l'articolo come ricevuto
        try {
          const quantity = parseInt(newProductData.quantityReceived) || 0;
          const productId = response.data.id;
          
          console.log('Segnare articolo come ricevuto:', {
            orderId: selectedItem.orderId,
            code: productCode, 
            quantityReceived: quantity,
            productId: productId
          });
          
          // Usiamo il nuovo prodotto creato per segnare la ricezione
          const receiveResponse = await api.put(`/orders/items/${selectedItem.orderId}/${productCode}/received`, {
            quantityReceived: quantity,
            productId: productId
          });
          
          console.log('Risposta ricezione dopo creazione prodotto:', receiveResponse.data);
          
          // IMPORTANTE: Non chiamare manualmente warehouse-movements/load qui!
          // Il backend (routes/orders.js) già aggiorna l'inventario e crea il movimento di magazzino 
          // quando un articolo viene segnato come ricevuto. Una chiamata aggiuntiva causerebbe un doppio caricamento.
          
          // Aggiorna la lista degli articoli da ricevere
          await loadPendingItems();
          
          setSnackbar({
            open: true,
            message: 'Prodotto creato e articolo ricevuto con successo',
            severity: 'success'
          });

          setNewProductDialog(false);
          setSelectedItem(null);
        } catch (err) {
          console.error('Errore nel segnare l\'articolo come ricevuto:', err);
          console.error('Dettagli errore:', err.response?.data);
          
          // Messaggio di errore più specifico in base alla risposta
          let errorMessage = 'Prodotto creato ma errore nel segnare come ricevuto';
          
          if (err.response?.status === 404) {
            errorMessage = 'Prodotto creato ma non è stato possibile trovare l\'articolo nell\'ordine';
          } else if (err.response?.status === 400) {
            if (err.response?.data?.error === 'QUANTITY_EXCEEDED') {
              errorMessage = 'Quantità ricevuta superiore a quella ordinata';
            } else if (err.response?.data?.message) {
              errorMessage = err.response.data.message;
            }
          }
          
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'warning'
          });
          
          // Anche se c'è un errore, chiudiamo il dialog poiché il prodotto è stato creato
          setNewProductDialog(false);
        }
      }
    } catch (err) {
      console.error('Errore nella creazione del prodotto:', err);
      console.error('Dettagli errore:', err.response?.data);
      console.error('Status code:', err.response?.status);
      console.error('Payload inviato:', err.config?.data);
      setSnackbar({
        open: true,
        message: `Errore nella creazione del prodotto: ${err.response?.data?.message || err.message} (${err.response?.status || 'unknown'})`,
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Gestione delle opzioni di prezzo
  const handlePriceOption = async (action) => {
    setLoading(true);
    
    try {
      if (action === 'none') {
        // Se l'utente sceglie di lasciare invariati i prezzi
        setPriceData(prev => ({
          ...prev,
          action: 'none'
        }));
        setPriceUpdateDialog(false);
        setQuantityReceivedDialog(true);
        setLoading(false);
        return;
      }
      
      // Se l'utente sceglie la media ponderata
      const response = await api.post('/products/update-prices-on-load', {
        productId: selectedItem.existingProduct.id,
        oldPurchasePrice: priceData.oldPurchasePrice,
        newPurchasePrice: priceData.newPurchasePrice,
        quantityLoaded: priceData.quantityLoaded,
        action,
        forceRecalculation: action === priceData.action
      });
      
      if (response.data && response.data.product) {
        const productData = response.data.product;
        
        const assemblyTime = productData.assemblyTime || priceData.assemblyTime;
        const laborCostOption = (assemblyTime / 60) * priceData.laborHourlyRate;
        const sellingPriceWithLabor = parseFloat(productData.updatedSellingPrice || 0) + laborCostOption;
        
        setPriceData({
          ...priceData,
          updatedPurchasePrice: parseFloat(productData.updatedPurchasePrice || 0),
          updatedSellingPrice: parseFloat(productData.updatedSellingPrice || 0),
          assemblyTime: parseFloat(assemblyTime) || 0, // Supporta decimali
          sellingPriceWithLabor: sellingPriceWithLabor,
          action
        });
      }
    } catch (err) {
      console.error('Errore nel calcolo prezzi:', err);
      setSnackbar({
        open: true,
        message: 'Errore durante il calcolo dei prezzi: ' + (err.response?.data?.error || err.message),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Calcola un nuovo prezzo di vendita in base al markup mantenuto
  const calculateNewSellingPrice = (newPurchasePrice, oldPurchasePrice, oldSellingPrice) => {
    // Se il prezzo di vendita o acquisto vecchio è zero, non possiamo calcolare il markup
    if (oldPurchasePrice <= 0 || oldSellingPrice <= 0) {
      return newPurchasePrice * 1.3; // Usiamo un markup del 30% come default
    }
    
    // Calcola il markup attuale
    const markup = oldSellingPrice / oldPurchasePrice;
    
    // Applica lo stesso markup al nuovo prezzo di acquisto
    return newPurchasePrice * markup;
  };

  // Conferma i prezzi aggiornati e procedi con la ricezione
  const confirmPrices = async () => {
    setLoading(true);
    setError('');
    try {
      const productId = selectedItem.existingProduct.id;
      const existingProduct = selectedItem.existingProduct;
      
      if (!existingProduct) {
        throw new Error('Prodotto non trovato');
      }
      
      const assemblyTimeValue = Math.max(0, parseFloat(priceData.assemblyTime) || 0); // Supporta decimali
      
      await api.put(`/products/${productId}`, {
        ...existingProduct,
        sellingPrice: priceData.updatedSellingPrice,
        purchasePrice: priceData.updatedPurchasePrice,
        assemblyTime: assemblyTimeValue
      });
      
      setPriceUpdateDialog(false);
      setQuantityReceivedDialog(true);
      
      setSnackbar({
        open: true,
        message: `Prezzo ${priceData.action === 'average' ? 'aggiornato con media ponderata' : 'forzato al nuovo valore'}`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Errore nell\'aggiornamento prezzi:', err);
      setSnackbar({
        open: true,
        message: 'Errore durante l\'aggiornamento prezzi: ' + (err.response?.data?.error || err.message),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Articoli da Ricevere
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Quando segni un articolo come ricevuto, il sistema verificherà se il prodotto esiste già nel magazzino.
        Se esiste, le scorte verranno aggiornate automaticamente; se non esiste, ti verrà chiesto di confermare i dettagli
        prima di aggiungerlo al catalogo.
      </Alert>

      {pendingItems.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            Nessun articolo in attesa di consegna
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Immagine</TableCell>
                <TableCell>Codice</TableCell>
                <TableCell>Descrizione</TableCell>
                <TableCell>Quantità Totale</TableCell>
                <TableCell>Quantità Ricevuta</TableCell>
                <TableCell>Quantità da Ricevere</TableCell>
                <TableCell>Prezzo Acquisto</TableCell>
                <TableCell>Ordine</TableCell>
                <TableCell>Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingItems
                .filter(item => {
                  const receivedQuantity = item.receivedQuantity || 0;
                  return receivedQuantity < item.quantity;
                })
                .map((item) => {
                  const receivedQuantity = item.receivedQuantity || 0;
                  const quantityToReceive = item.quantity - receivedQuantity;
                  
                  return (
                <TableRow key={`${item.orderId}-${item.code}`}>
                  <TableCell>
                    <CardMedia
                      component="img"
                      image={getProductImage(item.image)}
                      alt={item.description}
                      sx={{ width: 60, height: 60, objectFit: 'contain', bgcolor: '#f5f5f5', borderRadius: 1 }}
                      onError={(e) => {
                        console.warn(`Errore nel caricamento dell'immagine per prodotto ${item.id}`);
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFRUVFRUUiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzY2NiIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0Ij5JbW1hZ2luZSBub24gZGlzcG9uaWJpbGU8L3RleHQ+PC9zdmc+';
                      }}
                    />
                  </TableCell>
                  <TableCell>{item.code}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>{item.receivedQuantity || 0}</TableCell>
                  <TableCell>{item.quantity - (item.receivedQuantity || 0)}</TableCell>
                  <TableCell>€{typeof item.price === 'number' ? item.price.toFixed(2) : parseFloat(item.price || 0).toFixed(2)}</TableCell>
                  <TableCell>{item.orderNumber}</TableCell>
                  <TableCell>
                    <Tooltip title="Segna come ricevuto">
                      <IconButton
                        color="primary"
                        onClick={() => handleReceiveItem(item)}
                        disabled={(item.quantity - (item.receivedQuantity || 0)) <= 0}
                      >
                        <CheckIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog per nuovo prodotto */}
      <Dialog
        open={newProductDialog}
        onClose={() => setNewProductDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nuovo Prodotto</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Nome prodotto"
              value={newProductData.name}
              onChange={(e) => setNewProductData(prev => ({ ...prev, name: e.target.value.toUpperCase() }))}
              margin="normal"
              required
              inputProps={{ maxLength: 80 }}
              helperText={`${newProductData.name.length}/80 caratteri`}
            />
            <TextField
              fullWidth
              label="Descrizione"
              value={newProductData.description}
              onChange={(e) => setNewProductData(prev => ({ ...prev, description: e.target.value.toUpperCase() }))}
              margin="normal"
              multiline
              rows={2}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Categoria</InputLabel>
              <Select
                value={newProductData.category}
                onChange={(e) => setNewProductData(prev => ({ ...prev, category: e.target.value.toUpperCase() }))}
                label="Categoria"
                required
                error={!newProductData.category}
                helperText={!newProductData.category ? "La categoria è obbligatoria" : ""}
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Marca"
              value={newProductData.brand}
              onChange={(e) => setNewProductData(prev => ({ ...prev, brand: e.target.value.toUpperCase() }))}
              margin="normal"
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Prezzo di acquisto Iva esclusa"
                type="number"
                value={newProductData.purchasePrice}
                margin="normal"
                disabled
                inputProps={{ step: "0.01" }}
              />
              <TextField
                fullWidth
                label="Prezzo di acquisto Iva inclusa (22%)"
                type="number"
                value={(parseFloat(newProductData.purchasePrice || 0) * 1.22).toFixed(2)}
                margin="normal"
                disabled
              />
            </Box>
            <TextField
              fullWidth
              label="Prezzo di vendita"
              type="number"
              value={newProductData.sellingPrice}
              onChange={(e) => setNewProductData(prev => ({ ...prev, sellingPrice: e.target.value }))}
              margin="normal"
              required
              inputProps={{ step: "0.01" }}
            />
            <TextField
              fullWidth
              label="Tempo di montaggio (minuti) "
              type="number"
              value={newProductData.assemblyTime}
              onChange={handleAssemblyTimeChange}
              margin="normal"
              required
              inputProps={{ step: "0.1", min: "0" }}
            />
            <TextField
              fullWidth
              label="Prezzo montaggio incluso"
              type="number"
              value={newProductData.totalPriceWithLabor}
              onChange={handleTotalPriceChange}
              margin="normal"
              inputProps={{ step: "0.01" }}
            />
            <TextField
              fullWidth
              label="Quantità ricevuta"
              type="text"
              value={newProductData.quantityReceived}
              onChange={handleQuantityReceivedChange}
              margin="normal"
              required
              error={!!newProductData.quantityError}
              helperText={newProductData.quantityError || `Massimo: ${selectedItem?.quantity || 0} pezzi`}
              inputProps={{ 
                inputMode: 'numeric', 
                pattern: '[0-9]*',
                max: selectedItem?.quantity || 0
              }}
            />
            <TextField
              fullWidth
              label="Scorta minima"
              type="number"
              value={newProductData.minStock}
              onChange={(e) => setNewProductData(prev => ({ ...prev, minStock: e.target.value }))}
              margin="normal"
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewProductDialog(false)}>Annulla</Button>
          <Button 
            onClick={handleAddNewProduct}
            variant="contained"
            color="primary"
            disabled={
              !newProductData.name ||
              !newProductData.sellingPrice ||
              !newProductData.quantityReceived ||
              !newProductData.category ||
              !newProductData.minStock ||
              !newProductData.assemblyTime ||
              parseInt(newProductData.quantityReceived) <= 0 ||
              parseInt(newProductData.quantityReceived) > (selectedItem?.quantity || 0) ||
              !!newProductData.quantityError
            }
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog per quantità ricevuta */}
      <QuantityReceivedDialog
        open={quantityReceivedDialog}
        onClose={() => setQuantityReceivedDialog(false)}
        onConfirm={handleQuantityReceived}
        product={selectedItem}
      />

      {/* Dialog per aggiornamento prezzi */}
      <Dialog 
        open={priceUpdateDialog && priceData.action !== 'average'}
        onClose={() => !loading && setPriceUpdateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', mb: 2 }}>
          Variazione prezzo di acquisto
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ p: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', color: 'primary.main', mb: 3 }}>
              Il prezzo di acquisto è variato
            </Typography>
            
            <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" color="text.secondary">Prezzo precedente:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold">€{priceData.oldPurchasePrice.toFixed(2)}</Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="subtitle1" color="text.secondary">Nuovo prezzo:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold">€{priceData.newPurchasePrice.toFixed(2)}</Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="subtitle1" color="text.secondary">Variazione:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography 
                    variant="subtitle1"
                    fontWeight="bold"
                    color={priceData.newPurchasePrice > priceData.oldPurchasePrice ? "error.main" : "success.main"}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    {priceData.newPurchasePrice > priceData.oldPurchasePrice ? "+" : ""}
                    €{(priceData.newPurchasePrice - priceData.oldPurchasePrice).toFixed(2)}
                    <Typography 
                      component="span" 
                      variant="body2" 
                      sx={{ ml: 1, bgcolor: priceData.newPurchasePrice > priceData.oldPurchasePrice ? 'error.light' : 'success.light', 
                            px: 1, py: 0.5, borderRadius: 1 }}
                    >
                      {Math.round((priceData.newPurchasePrice - priceData.oldPurchasePrice) / priceData.oldPurchasePrice * 100)}%
                    </Typography>
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
            
            <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
              Come desideri gestire questa variazione di prezzo?
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
          <Button 
            onClick={() => setPriceUpdateDialog(false)} 
            variant="outlined" 
            color="error"
            sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}
          >
            ANNULLA
          </Button>
          <Box>
            <Button 
              onClick={() => handlePriceOption('average')} 
              variant="contained" 
              color="info" 
              disabled={loading}
              sx={{ 
                textTransform: 'uppercase', 
                fontWeight: 'bold', 
                mr: 1,
                bgcolor: 'primary.light'
              }}
            >
              MEDIA PONDERATA
            </Button>
            <Button 
              onClick={handleConfirmVariation} 
              variant="contained" 
              color="error" 
              disabled={loading}
              sx={{ 
                textTransform: 'uppercase', 
                fontWeight: 'bold',
                bgcolor: 'error.main'
              }}
            >
              {loading ? 'ELABORAZIONE...' : 'CONFERMA NUOVO PREZZO'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Dialog per media ponderata */}
      <Dialog
        open={priceUpdateDialog && priceData.action === 'average'}
        onClose={() => !loading && setPriceUpdateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', mb: 2 }}>
          Media Ponderata Prezzi
        </DialogTitle>
        
        <DialogContent>
          {priceUpdateDialog && priceData.action === 'average' && (
            <Box sx={{ p: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', color: 'primary.main', mb: 3 }}>
                Aggiornamento con la media ponderata
              </Typography>
              
              <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Prezzo di acquisto"
                      type="number"
                      fullWidth
                      margin="normal"
                      name="updatedPurchasePrice"
                      value={priceData.updatedPurchasePrice}
                      disabled
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                        readOnly: true,
                        sx: { bgcolor: 'action.hover', fontWeight: 'bold' }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <span className="material-icons-outlined" style={{ fontSize: '16px', marginRight: '4px' }}>info</span>
                      Vecchio prezzo: €{priceData.oldPurchasePrice.toFixed(2)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Tempo di manodopera"
                      type="number"
                      fullWidth
                      margin="normal"
                      name="assemblyTime"
                      value={priceData.assemblyTime}
                      onChange={(e) => {
                        const newTime = parseFloat(e.target.value) || 0; // Supporta decimali
                        const laborPrice = (newTime / 60) * priceData.laborHourlyRate;
                        setPriceData(prev => ({
                          ...prev,
                          assemblyTime: newTime,
                          sellingPriceWithLabor: prev.updatedSellingPrice + laborPrice
                        }));
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">min</InputAdornment>,
                        inputProps: { min: 0, step: "0.1" } // Supporta decimali
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <span className="material-icons-outlined" style={{ fontSize: '16px', marginRight: '4px' }}>euro</span>
                      Costo manodopera: €{((priceData.assemblyTime / 60) * priceData.laborHourlyRate).toFixed(2)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Prezzo di vendita"
                      type="number"
                      fullWidth
                      margin="normal"
                      name="updatedSellingPrice"
                      value={priceData.updatedSellingPrice}
                      onChange={(e) => {
                        const newPrice = parseFloat(e.target.value) || 0;
                        setPriceData(prev => ({
                          ...prev,
                          updatedSellingPrice: newPrice,
                          sellingPriceWithLabor: newPrice + ((prev.assemblyTime / 60) * prev.laborHourlyRate)
                        }));
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                        inputProps: { step: 0.01, min: 0 }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Prezzo finale con manodopera"
                      type="number"
                      fullWidth
                      margin="normal"
                      name="sellingPriceWithLabor"
                      value={priceData.sellingPriceWithLabor}
                      onChange={(e) => {
                        const newTotalPrice = parseFloat(e.target.value) || 0;
                        const basePrice = priceData.updatedSellingPrice;
                        
                        // Manteniamo il prezzo di vendita base invariato
                        // Calcoliamo il nuovo tempo di manodopera basato sulla differenza
                        const laborCost = Math.max(0, newTotalPrice - basePrice);
                        
                        // Calcolo dei minuti di manodopera in base al costo
                        // Costo manodopera = (minuti / 60) * tariffa oraria
                        // Quindi: minuti = (costo * 60) / tariffa oraria
                        const newAssemblyTime = (laborCost * 60) / priceData.laborHourlyRate; // Supporta decimali
                        
                        setPriceData(prev => ({
                          ...prev,
                          // Il prezzo di vendita base rimane invariato
                          assemblyTime: parseFloat(newAssemblyTime.toFixed(2)), // Mantiene 2 decimali
                          sellingPriceWithLabor: newTotalPrice
                        }));
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                        inputProps: { step: "0.01", min: 0 },
                        sx: { fontWeight: 'bold' }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                La media ponderata calcola un nuovo prezzo basato sulle quantità esistenti e quelle in arrivo,
                bilanciando i prezzi in modo proporzionale.
              </Alert>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
          <Button 
            onClick={() => setPriceUpdateDialog(false)} 
            variant="outlined" 
            color="error"
            sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}
          >
            CHIUDI
          </Button>
          <Button 
            onClick={handleConfirmPonderatedPrice} 
            variant="contained" 
            color="error" 
            disabled={loading}
            sx={{ 
              textTransform: 'uppercase', 
              fontWeight: 'bold',
              bgcolor: 'error.main'
            }}
          >
            {loading ? 'ELABORAZIONE...' : 'CONFERMA PREZZI'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PendingDeliveryTab; 